"use client";

import React from "react";
import { usePathname } from "next/navigation";
import Meta from "../Meta";
import Entity from "../Entity";
import {
  formatCategory,
  formatImageUrlByDevice,
  getPageNumberAsInt,
  _isFirstPage,
  _isLastPage,
  getLastPageNumber,
  previousLink,
  nextLink,
  getBasePageLink,
  formatUrl
} from "../../helpers/Helper";
import PropTypes from "prop-types";
import { useAppContext } from "@components/Provider";

import ContentPost from "@components/ContentPost/ContentPost";
import VCard from "@components/CardSlider/VCard";
import NewsletterV2 from "@components/NewsletterV2/NewsletterV2";
import Pagination from "@components/Pagination/Pagination";

import styles from "./ContentCategory.module.css";
// ==================== Function for populating SEO ====================
const populateSEO = (props, SiteSettings) => {
  let category = props && props.category,
    Site_Name = SiteSettings.site_name,
    SEO_Data = {
      Title: `${
        category && category[0].name
          ? formatCategory(category[0].name) + " | " + Site_Name
          : " "
      }`,
      Description:
        category && category[0].description
          ? category[0].description[0].description
          : " ",
      SailthruDescription:
        category && category[0].sailthru_description
          ? category[0]?.sailthru_description[0]?.sailthru_description
          : "",    
      SEOTitle:
        category && category[0].seotitle
          ? category[0].seotitle[0].seotitle
          : "",
      noIndex:
          category && category[0].seotitle
            ? category[0].seotitle[0].no_index
            : "",    
      SocialTitle:
        category && category[0].socialtitle
          ? category[0].socialtitle[0].socialtitle
          : "",
    };
  return SEO_Data;
};

// ==================== Function to render Title data =====================
const renderTitle = (props) => {
  let titleData = props;
  if (titleData && titleData.category) {
    let category = titleData.category[0];
    return (
      <div className="post-title">
        <h1 className={styles["category-name"]}>
          {category.name
            .replace(/-/g, " ")
            .replace("and", "&")
            .replace(/\\/g, "")
            .replace(/(tv)/, function (v) {
              return v.toUpperCase();
            })}
        </h1>
        <h2 className={styles.description}>
          {category !== undefined && category.description[0].description}
        </h2>
      </div>
    );
  }
};

// ======================= Function to Render Meta, Pagination and SiteSettings ===================================
const renderResults = (
  props,
  SiteSettings,
  currentPageNumber,
  router,
  host
) => {
  let resultData = props,
    totalRecords = (props && props.total) || 0,
    categoryLayout = SiteSettings.Category_Layout,
    route = router.pathname ? router.pathname : router.asPath,
    slugName = props.slug ? decodeURIComponent(props.slug) : "",
    pageSize = props.pageSize;
  currentPageNumber = getPageNumberAsInt(currentPageNumber);
  let isFirstPage = _isFirstPage(currentPageNumber),
    isLastPage = _isLastPage(currentPageNumber, totalRecords, pageSize);
  if (
    !resultData.posts ||
    isNaN(currentPageNumber) ||
    currentPageNumber > getLastPageNumber(totalRecords)
  ) {
    return <div></div>;
  } else {
    return (
      <div
        className={`${resultData.displayName} ${
          resultData.category ? ` CategoryPage` : ""
        } ${categoryLayout && ` alt-${categoryLayout}`}`}
      >
        <Meta
          url={props.pageUrl}
          static={populateSEO(props, SiteSettings)}
          pageType={Entity.CATEGORY}
          domain={host}
          isFirstPage={isFirstPage}
          isLastPage={isLastPage}
          previousLink={previousLink(
            props.pageName,
            slugName,
            currentPageNumber
          )}
          nextLink={nextLink(props.pageName, slugName, currentPageNumber)}
          isNewAppRouter={true}
        />
        <div className={styles.postCardContainer}>
          <ContentPost props={{ ...props, isCategoryPage: true }} />
        </div>
        <Pagination
          isFirstPage={isFirstPage}
          isLastPage={isLastPage}
          basePageLink={getBasePageLink(props.pageName, slugName)}
          currentPageNumber={currentPageNumber}
          lastPageNumber={getLastPageNumber(totalRecords, pageSize)}
          getLinkHref={route}
          paginationText=''
          pageSize={pageSize}
        />
      </div>
    );
  }
};

// ==================== Function to render Pinned Posts ====================
const renderPinnedPosts = (props) => {
  let banner = [],
    topStories = [],
    postData = props,
    posts =
      postData &&
      postData.category &&
      postData.category[0].components[0].posts.filter((el) => {
        if (
          postData.category[0].components[0].site_id ===
          postData.category[0].description[0].site_id
        ) {
          return el;
        }
      });
  for (let i in posts) {
    props.category[0].components[0].posts.forEach((post, idx) => {
      if(post) {
        let card = (
          <VCard
            key={`pin_post-` + idx}
            vkey={`pin_post-` + idx}
            slug={"/" + formatUrl(post._id && post._id.slug,post._id && post._id.redirect)}
            title={post.Title}
            subTitle={posts[i].checked ? post._id && post._id.sub_title : ""}
            image={formatImageUrlByDevice(
              post._id && post._id.featured_media.name,
              props.deviceType
            )}
            byline={post._id && post._id.byline}
            categories={post.categories ? post.categories : null}
            tags={
              post._id && post._id.tags && post._id.tags.length > 0
                ? post._id.tags
                : null
            }
            noAuthor={
              post._id && post._id.byline &&
              post._id.byline !== null &&
              post._id.byline.map((by) => by.display_name)
                ? false
                : true
            }
            isCategoryPage={true}
            checked={post.checked}
            tagLinkCategoryClasses={`${styles.tagLink} ${styles.categoryOther}`}
            vCardClasses={styles["v-card"]}
            imageClasses={styles.image}
            contentClasses={styles.content}
            authorClasses={styles.author}
            cAuthorNameClasses={styles.cAuthorName}
            categoryAuthorClasses={styles.categoryAuthor}
          />
        );
        if (posts[i].checked === true && posts[i]._id === post._id) {
          banner.push(card);
        } else {
          if (posts[i].checked !== true && posts[i]._id === post._id) {
            topStories.push(card);
          }
        }
      } 
    });
  }

  if (banner.length && topStories.length) {
    let topStories02 = topStories.splice(0, 2);
    return (
      <div className={`${styles["banner-container"]} banner-container`}>
        <div className={`${styles["part-8"]} part-8`}>{banner}</div>
        <div className={`${styles["part-4"]} part-4`}>
          <div className={`${styles["part-6"]} part-6`}>
            {topStories02}
          </div>
          <div className={`${styles["part-6"]} part-6`}>
            {topStories}
          </div>
        </div>
      </div>
    );
  }
};

const onSignUpToggle = (props) => {
  props.showNewsletter;
};

// ==================== Function to Render Newsletter part ====================
const renderNewsletter = (props, SiteSetting) => {
  let newsletterData = props,
    newsletterOptions =
      newsletterData &&
      newsletterData.category &&
      newsletterData.category[0].components.filter((el) => {
        if (
          newsletterData.category[0].components[0].site_id ===
          newsletterData.category[0].description[0].site_id
        ) {
          return el;
        }
      });
  
  if (newsletterOptions[0].newsletter_options !== undefined) {
    return (
      <NewsletterV2
        onSignUpToggle={onSignUpToggle(props)}
        isChild={true}
        classNames={styles.ContentNewsletter}
        Newsletter_Settings={SiteSetting.Newsletter_Settings}
        {...newsletterOptions[0].newsletter_options.props}
        isCategoryPage={true}
        className={props.newsletterClass}
        siteId={props.siteId}
        newsletterCopyClass={styles["newsletter-copy"]}
        inputBoxClasses={styles.inputBox}
        formGroupClasses={styles.formGroup}
        buttonBoxClasses={styles.buttonBox}
        newLetterContainerClasses={styles.newLetterContainer}
      />
    );
  }
};

// ==================== Functional Component ====================
const ContentCategory = (props) => {
  
  //Reading sitesetting from context defined in _app.js
  let content = (props && props.contentData) || {};
  content = (content && content.data) || {};
  const [contentCategory] = content;
  content = (contentCategory && contentCategory.Category) || {};
  const pathname = usePathname();
  const context = useAppContext();
  if (!context) {
      console.error("ContentCaetgory: Invalid context:", context);
      console.error("ContentCaetgory: pathname :", pathname);
      throw error; // Or handle gracefully
  }    

  const {
    data,
    url,
    router,
    host,
    subscriberCookie,
    deviceType,
    siteId
  } = context || {};
  props = { ...content, ...props.contentData, deviceType };
  let { SiteSettings } = data || {},
    currentPageNumber = getPageNumberAsInt(props.pageNumber);
  return (
    <div className={`${styles.categorySection} ${subscriberCookie ? styles.topPaddingWithCookie : styles.topPaddingWithoutCookie} topPadding`}>
      <div className={`category-title-container ${styles.titleSection}`}>
        {renderTitle(props)}
      </div>
      <div className="pinned-posts-container">{renderPinnedPosts(props)}</div>
      {!subscriberCookie && (
        <div className={styles.newsletterSection}>
          {renderNewsletter(props, SiteSettings) !== undefined && (
            <div className="category-newsletter-container">
              <div className={styles.cnewsletterContainer}>
                {renderNewsletter(props, SiteSettings)}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="cardSection">
        {renderResults(props, SiteSettings, currentPageNumber, router, host)}
      </div>
    </div>
  );
};

ContentCategory.propTypes = {
  props: PropTypes.object,
};

export default ContentCategory;
